/* eslint-disable @typescript-eslint/no-explicit-any -- legacy untyped Supabase joins; replace with generated types (tech debt) */
/**
 * Credentials Service
 * Handles all credential request-related database operations
 */

import { supabase, type PaginatedResponse, type SortOption } from './supabaseClient';
import { CredentialRequest, CredentialRequestMinister } from '@/models/credentialRequest';
import { Minister } from '@/models/minister';
import { Database } from '@/types/supabase';
import { NotFoundError, DatabaseError, ValidationError } from '@/lib/errors';
import { PAGINATION } from '@/constants';

// Type helpers for inserts and updates
type CredentialRequestInsert = Database['public']['Tables']['credential_requests']['Insert'];
type CredentialRequestUpdate = Database['public']['Tables']['credential_requests']['Update'];

/**
 * Filters for credential request queries
 */
export interface CredentialRequestFilters {
  status?: string;
  districtId?: string;
  regionId?: string;
  createdBy?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  credentialType?: string;
  institutionId?: string;
}

/**
 * Get paginated list of credential requests with filters and sorting
 * @throws {DatabaseError} If the database query fails
 */
export async function getCredentialRequestsPaginated(
  page: number = 1,
  pageSize: number = PAGINATION.DEFAULT_PAGE_SIZE,
  filters?: CredentialRequestFilters,
  sort?: SortOption
): Promise<PaginatedResponse<CredentialRequest>> {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('credential_requests')
      .select(`
        *,
        region:regions(id, name, code),
        district:districts(id, name, code),
        creator:users!created_by(id, full_name),
        minister_count:credential_request_ministers(count)
      `, { count: 'exact' });

    // Apply filters
    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.districtId) {
      query = query.eq('district_id', filters.districtId);
    }

    if (filters?.regionId) {
      query = query.eq('region_id', filters.regionId);
    }

    if (filters?.createdBy) {
      query = query.eq('created_by', filters.createdBy);
    }

    if (filters?.credentialType) {
      query = query.eq('credential_type', filters.credentialType);
    } else {
      query = query.eq('credential_type', 'MINISTERIAL');
    }

    if (filters?.institutionId) {
      query = query.eq('institution_id', filters.institutionId);
    }

    // Date filters receive ISO 8601 timestamps in UTC
    if (filters?.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }

    if (filters?.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    if (filters?.search) {
      // Search for requests that match:
      // 1. Credential request code (partial match)
      // 2. Notes (partial match)
      // 3. Minister adb_code (exact match via join)
      
      // First, get minister IDs that match the adb_code
      const { data: ministers } = await supabase
        .from('ministers')
        .select('id')
        .eq('adb_code', filters.search);

      const ministerIds = (ministers as { id: string }[] | null)?.map(m => m.id) || [];

      // Then get request IDs that have those ministers
      let requestIdsWithMinister: string[] = [];
      if (ministerIds.length > 0) {
        const { data: ministerRequests } = await supabase
          .from('credential_request_ministers')
          .select('credential_request_id')
          .in('minister_id', ministerIds);

        requestIdsWithMinister = (ministerRequests as { credential_request_id: string }[] | null)?.map(r => r.credential_request_id) || [];
      }

      // Build the search query
      if (requestIdsWithMinister.length > 0) {
        // Search by code/notes OR by request IDs that contain the minister
        query = query.or(
          `code.ilike.%${filters.search}%,` +
          `notes.ilike.%${filters.search}%,` +
          `id.in.(${requestIdsWithMinister.join(',')})`
        );
      } else {
        // Only search by code/notes if no minister match
        query = query.or(
          `code.ilike.%${filters.search}%,` +
          `notes.ilike.%${filters.search}%`
        );
      }
    }

    // Apply sorting
    if (sort?.field) {
      // Skip sorting for joined fields that can't be sorted directly
      const unsortableFields = ['district.name', 'region.name', 'created_by'];
      
      if (!unsortableFields.includes(sort.field)) {
        query = query.order(sort.field, { ascending: sort.direction === 'asc' });
      }
    } else {
      query = query.order('created_at', { ascending: false });
    }

    // Apply pagination
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) throw error;

    return {
      data: (data || []) as unknown as CredentialRequest[],
      count: count || 0,
      page,
      pageSize,
      totalPages: Math.ceil((count || 0) / pageSize)
    };
  } catch (error) {
    throw new DatabaseError('Failed to fetch credential requests', error);
  }
}

/**
 * Get a credential request by ID with full relations
 * @throws {NotFoundError} If credential request is not found
 * @throws {DatabaseError} If the database query fails
 */
export async function getCredentialRequestById(id: string): Promise<CredentialRequest | null> {
  try {
    const { data, error } = await supabase
      .from('credential_requests')
      .select(`
        *,
        region:regions(id, name, code),
        district:districts(id, name, code),
        creator:users!created_by(id, full_name, email, role:roles(code)),
        approver:users!approved_by(id, full_name),
        rejecter:users!rejected_by(id, full_name),
        printer:users!printed_by(id, full_name),
        deliverer:users!delivered_by(id, full_name)
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`Credential request with ID ${id} not found`);
      }
      throw error;
    }

    return data as unknown as CredentialRequest;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to fetch credential request with ID ${id}`, error);
  }
}

/**
 * Create a new credential request
 * @throws {DatabaseError} If the database operation fails
 */
export async function createCredentialRequest(
  request: CredentialRequestInsert
): Promise<CredentialRequest> {
  try {
    const { data, error } = await supabase
      .from('credential_requests')
      .insert(request as never)
      .select(`
        *,
        region:regions(id, name, code),
        district:districts(id, name, code)
      `)
      .single();

    if (error) throw error;
    return data as CredentialRequest;
  } catch (error) {
    throw new DatabaseError('Failed to create credential request', error);
  }
}

/**
 * Update an existing credential request
 * @throws {NotFoundError} If credential request is not found
 * @throws {DatabaseError} If the database operation fails
 */
export async function updateCredentialRequest(
  id: string,
  updates: CredentialRequestUpdate
): Promise<CredentialRequest> {
  try {
    const { data, error } = await supabase
      .from('credential_requests')
      .update(updates as never)
      .eq('id', id)
      .select(`
        *,
        region:regions(id, name, code),
        district:districts(id, name, code)
      `)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`Credential request with ID ${id} not found`);
      }
      throw error;
    }

    return data as CredentialRequest;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to update credential request with ID ${id}`, error);
  }
}

/**
 * Delete a credential request and its associated ministers
 * @throws {DatabaseError} If the database operation fails
 */
export async function deleteCredentialRequest(id: string): Promise<void> {
  try {
    // First delete all junction table entries
    await supabase
      .from('credential_request_ministers')
      .delete()
      .eq('credential_request_id', id);

    // Then delete the request
    const { error } = await supabase
      .from('credential_requests')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    throw new DatabaseError(`Failed to delete credential request with ID ${id}`, error);
  }
}

// ============================================================================
// CREDENTIAL REQUEST WORKFLOW STATE TRANSITIONS
// ============================================================================

/**
 * Check if a minister is already in an active credential request for a specific year
 * Active states: SUBMITTED, APPROVED, PRINTED, DELIVERED
 * Ministers can be in multiple active requests only if they are from different years
 * @throws {DatabaseError} If the database query fails
 */
export async function isMinisterInActiveRequest(ministerId: string, year: number): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('credential_request_ministers')
      .select(`
        credential_request:credential_requests!inner(status, year)
      `)
      .eq('minister_id', ministerId)
      .eq('credential_requests.year', year)
      .in('credential_requests.status', ['SUBMITTED', 'APPROVED', 'PRINTED', 'DELIVERED']);

    if (error) throw error;
    return (data?.length || 0) > 0;
  } catch (error) {
    throw new DatabaseError('Failed to check minister active requests', error);
  }
}

/**
 * Submit a credential request (DRAFT/REJECTED → SUBMITTED)
 * Validates: at least 1 minister, no ministers in other active requests for the same year
 * @throws {NotFoundError} If credential request is not found
 * @throws {ValidationError} If validation fails (no ministers, status invalid, minister in active request)
 * @throws {DatabaseError} If the database operation fails
 */
export async function submitCredentialRequest(
  requestId: string
): Promise<CredentialRequest> {
  try {
    // Get the request with its ministers
    const request = await getCredentialRequestById(requestId);
    if (!request) {
      throw new NotFoundError(`Credential request with ID ${requestId} not found`);
    }

    if (request.status !== 'DRAFT' && request.status !== 'REJECTED') {
      throw new ValidationError('Only DRAFT or REJECTED requests can be submitted');
    }

    // Get ministers for this request
    const ministers = await getMinistersByCredentialRequest(requestId);
    
    if (ministers.length === 0) {
      throw new ValidationError('Cannot submit request without ministers');
    }

    // Check if any ministers are in active requests for the same year
    const requestYear = request.year || new Date().getFullYear();
    for (const minister of ministers) {
      const isActive = await isMinisterInActiveRequest(minister.id, requestYear);
      if (isActive) {
        throw new ValidationError(
          `Minister ${minister.full_name} is already in an active credential request for year ${requestYear}`
        );
      }
    }

    // Update status
    const { data, error } = await supabase
      .from('credential_requests')
      .update({
        status: 'SUBMITTED',
        submitted_at: new Date().toISOString()
      } as never)
      .eq('id', requestId)
      .select(`
        *,
        region:regions(id, name, code),
        district:districts(id, name, code),
        created_by_user:users!credential_requests_created_by_fkey(id, full_name, email),
        approved_by_user:users!credential_requests_approved_by_fkey(id, full_name, email),
        rejected_by_user:users!credential_requests_rejected_by_fkey(id, full_name, email),
        printed_by_user:users!credential_requests_printed_by_fkey(id, full_name, email),
        delivered_by_user:users!credential_requests_delivered_by_fkey(id, full_name, email)
      `)
      .single();

    if (error) throw error;
    return data as CredentialRequest;
  } catch (error) {
    if (error instanceof NotFoundError || error instanceof ValidationError) throw error;
    throw new DatabaseError('Failed to submit credential request', error);
  }
}

/**
 * Approve a credential request (SUBMITTED → APPROVED)
 * Only admins can approve
 * @throws {NotFoundError} If credential request is not found
 * @throws {ValidationError} If status is not SUBMITTED
 * @throws {DatabaseError} If the database operation fails
 */
export async function approveCredentialRequest(
  requestId: string,
  userId: string
): Promise<CredentialRequest> {
  try {
    const request = await getCredentialRequestById(requestId);
    if (!request) {
      throw new NotFoundError(`Credential request with ID ${requestId} not found`);
    }

    if (request.status !== 'SUBMITTED') {
      throw new ValidationError('Only SUBMITTED requests can be approved');
    }

    const { data, error } = await supabase
      .from('credential_requests')
      .update({
        status: 'APPROVED',
        approved_at: new Date().toISOString(),
        approved_by: userId
      } as never)
      .eq('id', requestId)
      .select(`
        *,
        region:regions(id, name, code),
        district:districts(id, name, code),
        created_by_user:users!credential_requests_created_by_fkey(id, full_name, email),
        approved_by_user:users!credential_requests_approved_by_fkey(id, full_name, email),
        rejected_by_user:users!credential_requests_rejected_by_fkey(id, full_name, email),
        printed_by_user:users!credential_requests_printed_by_fkey(id, full_name, email),
        delivered_by_user:users!credential_requests_delivered_by_fkey(id, full_name, email)
      `)
      .single();

    if (error) throw error;
    return data as CredentialRequest;
  } catch (error) {
    if (error instanceof NotFoundError || error instanceof ValidationError) throw error;
    throw new DatabaseError('Failed to approve credential request', error);
  }
}

/**
 * Reject a credential request (SUBMITTED → REJECTED)
 * Only admins can reject
 * @throws {NotFoundError} If credential request is not found
 * @throws {ValidationError} If status is not SUBMITTED
 * @throws {DatabaseError} If the database operation fails
 */
export async function rejectCredentialRequest(
  requestId: string,
  userId: string,
  observations?: string
): Promise<CredentialRequest> {
  try {
    const request = await getCredentialRequestById(requestId);
    if (!request) {
      throw new NotFoundError(`Credential request with ID ${requestId} not found`);
    }

    if (request.status !== 'SUBMITTED') {
      throw new ValidationError('Only SUBMITTED requests can be rejected');
    }

    const { data, error } = await supabase
      .from('credential_requests')
      .update({
        status: 'REJECTED',
        rejected_at: new Date().toISOString(),
        rejected_by: userId,
        observations: observations || null
      } as never)
      .eq('id', requestId)
      .select(`
        *,
        region:regions(id, name, code),
        district:districts(id, name, code),
        created_by_user:users!credential_requests_created_by_fkey(id, full_name, email),
        approved_by_user:users!credential_requests_approved_by_fkey(id, full_name, email),
        rejected_by_user:users!credential_requests_rejected_by_fkey(id, full_name, email),
        printed_by_user:users!credential_requests_printed_by_fkey(id, full_name, email),
        delivered_by_user:users!credential_requests_delivered_by_fkey(id, full_name, email)
      `)
      .single();

    if (error) throw error;
    return data as CredentialRequest;
  } catch (error) {
    if (error instanceof NotFoundError || error instanceof ValidationError) throw error;
    throw new DatabaseError('Failed to reject credential request', error);
  }
}

/**
 * Mark a credential request as printed (APPROVED → PRINTED)
 * Only admins can mark as printed
 * @throws {NotFoundError} If credential request is not found
 * @throws {ValidationError} If status is not APPROVED
 * @throws {DatabaseError} If the database operation fails
 */
export async function markCredentialRequestAsPrinted(
  requestId: string,
  userId: string
): Promise<CredentialRequest> {
  try {
    const request = await getCredentialRequestById(requestId);
    if (!request) {
      throw new NotFoundError(`Credential request with ID ${requestId} not found`);
    }

    if (request.status !== 'APPROVED') {
      throw new ValidationError('Only APPROVED requests can be marked as printed');
    }

    const { data, error } = await supabase
      .from('credential_requests')
      .update({
        status: 'PRINTED',
        printed_at: new Date().toISOString(),
        printed_by: userId
      } as never)
      .eq('id', requestId)
      .select(`
        *,
        region:regions(id, name, code),
        district:districts(id, name, code),
        created_by_user:users!credential_requests_created_by_fkey(id, full_name, email),
        approved_by_user:users!credential_requests_approved_by_fkey(id, full_name, email),
        rejected_by_user:users!credential_requests_rejected_by_fkey(id, full_name, email),
        printed_by_user:users!credential_requests_printed_by_fkey(id, full_name, email),
        delivered_by_user:users!credential_requests_delivered_by_fkey(id, full_name, email)
      `)
      .single();

    if (error) throw error;
    return data as CredentialRequest;
  } catch (error) {
    if (error instanceof NotFoundError || error instanceof ValidationError) throw error;
    throw new DatabaseError('Failed to mark credential request as printed', error);
  }
}

/**
 * Mark a credential request as delivered (PRINTED → DELIVERED)
 * Only admins can mark as delivered
 * @throws {NotFoundError} If credential request is not found
 * @throws {ValidationError} If status is not PRINTED
 * @throws {DatabaseError} If the database operation fails
 */
export async function markCredentialRequestAsDelivered(
  requestId: string,
  userId: string
): Promise<CredentialRequest> {
  try {
    const request = await getCredentialRequestById(requestId);
    if (!request) {
      throw new NotFoundError(`Credential request with ID ${requestId} not found`);
    }

    if (request.status !== 'PRINTED') {
      throw new ValidationError('Only PRINTED requests can be marked as delivered');
    }

    const { data, error } = await supabase
      .from('credential_requests')
      .update({
        status: 'DELIVERED',
        delivered_at: new Date().toISOString(),
        delivered_by: userId
      } as never)
      .eq('id', requestId)
      .select(`
        *,
        region:regions(id, name, code),
        district:districts(id, name, code),
        created_by_user:users!credential_requests_created_by_fkey(id, full_name, email),
        approved_by_user:users!credential_requests_approved_by_fkey(id, full_name, email),
        rejected_by_user:users!credential_requests_rejected_by_fkey(id, full_name, email),
        printed_by_user:users!credential_requests_printed_by_fkey(id, full_name, email),
        delivered_by_user:users!credential_requests_delivered_by_fkey(id, full_name, email)
      `)
      .single();

    if (error) throw error;
    return data as CredentialRequest;
  } catch (error) {
    if (error instanceof NotFoundError || error instanceof ValidationError) throw error;
    throw new DatabaseError('Failed to mark credential request as delivered', error);
  }
}

// ============================================================================
// CREDENTIAL REQUEST MINISTERS (Junction Table)
// ============================================================================

/**
 * Get all ministers associated with a credential request
 * @throws {DatabaseError} If the database query fails
 */
export async function getMinistersByCredentialRequest(requestId: string): Promise<Minister[]> {
  try {
    const { data, error } = await supabase
      .from('credential_request_ministers')
      .select(`
        minister:ministers(
          *,
          degree:degrees(id, name),
          district:districts!ministers_district_id_fkey(id, name, code),
          region:regions!ministers_region_id_fkey(id, name, code)
        ),
        institution_member:institution_members(id, full_name, dni)
      `)
      .eq('credential_request_id', requestId);

    if (error) throw error;

    return (data || []).map((item: any) => {
      if (item.minister) return item.minister;
      if (item.institution_member) {
        return {
          ...item.institution_member,
          adb_code: 'Miembro',
          degree: null,
          district: null,
          region: null,
        };
      }
      return null;
    }).filter(Boolean) as unknown as Minister[];
  } catch (error) {
    throw new DatabaseError('Failed to fetch ministers for credential request', error);
  }
}

/**
 * Add a minister to a credential request
 * @throws {DatabaseError} If the database operation fails
 */
export async function addMinisterToCredentialRequest(
  requestId: string,
  ministerId: string
): Promise<CredentialRequestMinister> {
  try {
    const { data, error } = await supabase
      .from('credential_request_ministers')
      .insert({ credential_request_id: requestId, minister_id: ministerId } as never)
      .select()
      .single();

    if (error) throw error;
    return data as CredentialRequestMinister;
  } catch (error) {
    throw new DatabaseError('Failed to add minister to credential request', error);
  }
}

/**
 * Add multiple ministers to a credential request in a single batch operation
 * More efficient than calling addMinisterToCredentialRequest multiple times
 * @throws {DatabaseError} If the database operation fails
 */
export async function addMinistersToCredentialRequest(
  requestId: string,
  ministerIds: string[]
): Promise<CredentialRequestMinister[]> {
  try {
    // Defense Layer 2: Check for existing ministers first to avoid duplicates
    const { data: existing } = await supabase
      .from('credential_request_ministers')
      .select('minister_id')
      .eq('credential_request_id', requestId)
      .in('minister_id', ministerIds);
    
    const existingIds = new Set(existing?.map((e: { minister_id: string | null }) => e.minister_id).filter(Boolean) || []);
    const newMinisterIds = ministerIds.filter(id => !existingIds.has(id));
    
    // If all ministers already exist, return empty array
    if (newMinisterIds.length === 0) {
      console.warn('[addMinistersToCredentialRequest] All ministers already in request, skipping insert');
      return [];
    }
    
    const records = newMinisterIds.map(ministerId => ({
      credential_request_id: requestId,
      minister_id: ministerId
    }));

    const { data, error } = await supabase
      .from('credential_request_ministers')
      .insert(records as never)
      .select();

    if (error) throw error;
    return data as CredentialRequestMinister[];
  } catch (error) {
    throw new DatabaseError('Failed to add ministers to credential request', error);
  }
}

/**
 * Remove a minister from a credential request
 * @throws {DatabaseError} If the database operation fails
 */
export async function removeMinisterFromCredentialRequest(
  requestId: string,
  ministerId: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('credential_request_ministers')
      .delete()
      .eq('credential_request_id', requestId)
      .eq('minister_id', ministerId);

    if (error) throw error;
  } catch (error) {
    throw new DatabaseError('Failed to remove minister from credential request', error);
  }
}

/**
 * Get all credential requests for a specific minister
 * @throws {DatabaseError} If the database query fails
 */
export async function getCredentialRequestsForMinister(ministerId: string): Promise<CredentialRequest[]> {
  try {
    const { data, error } = await supabase
      .from('credential_request_ministers')
      .select(`
        credential_request:credential_requests(
          *,
          region:regions(id, name, code),
          district:districts(id, name, code)
        )
      `)
      .eq('minister_id', ministerId);

    if (error) throw error;

    // Extract credential requests from nested structure
    return (data || []).map((item: any) => item.credential_request).filter(Boolean) as unknown as CredentialRequest[];
  } catch (error) {
    throw new DatabaseError('Failed to fetch credential requests for minister', error);
  }
}

/**
 * Get the next available code number for a given year
 * Returns the next sequential number based on the last code used for that year
 * @param year - The year to get the next code for (e.g., 2026)
 * @returns The next available code number (e.g., 451 if last was SOL26-0450)
 * @throws {DatabaseError} If the database query fails
 */
export async function getNextAvailableCodeNumber(year: number): Promise<number> {
  try {
    // Get the last 2 digits of the year for the prefix pattern
    const yearSuffix = year.toString().slice(-2);
    const codePrefix = `SOL${yearSuffix}-`;
    
    // Query all codes for this year, ordered by code descending
    const { data, error } = await supabase
      .from('credential_requests')
      .select('code')
      .eq('year', year)
      .not('code', 'is', null)
      .like('code', `${codePrefix}%`)
      .order('code', { ascending: false })
      .limit(1);
    
    if (error) throw error;
    
    // If no codes exist for this year, start at 1
    if (!data || data.length === 0) {
      return 1;
    }
    
    // Extract the number from the last code (e.g., "SOL26-0450" -> 450)
    const lastCode = (data[0] as { code: string | null }).code;
    const match = lastCode?.match(/SOL\d{2}-(\d+)$/);
    
    if (!match) {
      // If format doesn't match, start at 1
      return 1;
    }
    
    const lastNumber = parseInt(match[1], 10);
    return lastNumber + 1;
  } catch (error) {
    throw new DatabaseError('Failed to get next available code number', error);
  }
}

/**
 * Check if a credential request code already exists
 * @param code - The code to check (e.g., "SOL26-0450")
 * @param excludeRequestId - Optional request ID to exclude from the check (for updates)
 * @returns True if the code exists, false otherwise
 * @throws {DatabaseError} If the database query fails
 */
export async function codeExists(code: string, excludeRequestId?: string): Promise<boolean> {
  try {
    let query = supabase
      .from('credential_requests')
      .select('id')
      .eq('code', code)
      .limit(1);
    
    if (excludeRequestId) {
      query = query.neq('id', excludeRequestId);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    return (data && data.length > 0) || false;
  } catch (error) {
    throw new DatabaseError('Failed to check if code exists', error);
  }
}

// ============================================================================
// FUNCTION CREDENTIAL REQUESTS
// ============================================================================

/**
 * Auto-create all Function credential requests for all districts and regions
 * Creates empty requests in APPROVED state with auto-generated codes
 * @throws {DatabaseError} If the database operation fails
 */
export async function autoCreateFunctionRequests(year: number, userId: string): Promise<{ created: number; updated: number }> {
  try {
    const updated = 0;

    // Batch optimization: Get all data in parallel
    const [districtsResult, existingRequestsResult] = await Promise.all([
      // Get all active districts with their regions
      supabase
        .from('districts')
        .select(`
          id,
          name,
          code,
          superintendent_id,
          vice_superintendent_id,
          secretary_id,
          treasurer_id,
          vocal1_id,
          vocal2_id,
          regions(
            id,
            name,
            code,
            presbyter_id,
            vice_presbyter_id,
            secretary_id,
            treasurer_id,
            vocal1_id,
            vocal2_id
          )
        `) as unknown as Promise<{ data: any[] | null; error: any }>,
      
      // Get all existing function requests for this year
      supabase
        .from('credential_requests')
        .select('district_id, region_id, institution_id')
        .eq('credential_type', 'FUNCTION')
        .eq('year', year) as unknown as Promise<{ data: any[] | null; error: any }>
    ]);

    if (districtsResult.error) throw districtsResult.error;
    if (existingRequestsResult.error) throw existingRequestsResult.error;

    const districts = districtsResult.data || [];
    const existingRequests = existingRequestsResult.data || [];

    // Build a Set of existing combinations for fast lookup
    // Format: "districtId|regionId" (regionId is "null" for district requests)
    const existingSet = new Set(
      existingRequests.map(req => `${req.district_id}|${req.region_id || 'null'}`)
    );

    // Build array of requests to create
    const requestsToCreate: any[] = [];
    const now = new Date().toISOString();

    for (const district of districts) {
      // Add district request if it doesn't exist
      const districtKey = `${district.id}|null`;
      if (!existingSet.has(districtKey)) {
        requestsToCreate.push({
          year,
          credential_type: 'FUNCTION',
          status: 'APPROVED', // Auto-approve function requests (trigger will generate code)
          created_by: userId,
          approved_by: userId,
          approved_at: now,
          district_id: district.id,
          region_id: null,
        });
      }

      // Add region requests if they don't exist
      const regions = district.regions || [];
      for (const region of regions) {
        const regionKey = `${district.id}|${region.id}`;
        if (!existingSet.has(regionKey)) {
          requestsToCreate.push({
            year,
            credential_type: 'FUNCTION',
            status: 'APPROVED', // Auto-approve function requests (trigger will generate code)
            created_by: userId,
            approved_by: userId,
            approved_at: now,
            district_id: district.id,
            region_id: region.id,
          });
        }
      }
    }

    // Batch insert all new requests in a single operation
    let created = 0;
    if (requestsToCreate.length > 0) {
      const { error: insertError } = await supabase
        .from('credential_requests')
        .insert(requestsToCreate as never);

      // Ignore unique constraint violations (concurrent creation edge case)
      if (insertError && insertError.code !== '23505') throw insertError;
      if (!insertError) created = requestsToCreate.length;
    }

    // Also create DN national request if it doesn't exist
    const { data: dnInstitution } = await supabase
      .from('institutions')
      .select('id')
      .eq('slug', 'directorio-nacional')
      .single();

    if (dnInstitution) {
      const existingDN = existingRequests.some(
        (r: any) => r.district_id === null && r.region_id === null && r.institution_id === dnInstitution.id,
      );
      if (!existingDN) {
        const nextNum = await getNextAvailableCodeNumberByPrefix(year, 'DN');
        const yearSuffix = year.toString().slice(-2);
        const dnCode = `SOL${yearSuffix}-DN-${String(nextNum).padStart(2, '0')}`;
        const { error: dnError } = await supabase.from('credential_requests').insert({
          institution_id: dnInstitution.id,
          credential_type: 'FUNCTION',
          year,
          status: 'APPROVED',
          code: dnCode,
          created_by: userId,
          approved_by: userId,
          approved_at: now,
          district_id: null,
          region_id: null,
        } as never);
        if (dnError && dnError.code !== '23505') throw dnError;
        if (!dnError) created++;
      }
    }

    return { created, updated };
  } catch (error) {
    throw new DatabaseError('Failed to auto-create function requests', error);
  }
}

/**
 * Sync authority ministers to function credential requests
 * Updates the ministers in function requests based on current authority assignments
 * @throws {DatabaseError} If the database operation fails
 */
export async function syncFunctionRequestMinisters(year: number): Promise<{ synced: number }> {
  try {
    // Batch optimization: Get all data in parallel
    const [requestsResult, districtsResult, regionsResult] = await Promise.all([
      // Get all function requests for the year
      supabase
        .from('credential_requests')
        .select('id, district_id, region_id')
        .eq('credential_type', 'FUNCTION')
        .eq('year', year) as unknown as Promise<{ data: any[] | null; error: any }>,
      
      // Get all districts with authority assignments
      supabase
        .from('districts')
        .select('id, superintendent_id, vice_superintendent_id, secretary_id, treasurer_id, vocal1_id, vocal2_id') as unknown as Promise<{ data: any[] | null; error: any }>,
      
      // Get all regions with authority assignments
      supabase
        .from('regions')
        .select('id, presbyter_id, vice_presbyter_id, secretary_id, treasurer_id, vocal1_id, vocal2_id') as unknown as Promise<{ data: any[] | null; error: any }>
    ]);

    if (requestsResult.error) throw requestsResult.error;
    if (districtsResult.error) throw districtsResult.error;
    if (regionsResult.error) throw regionsResult.error;

    const requests = requestsResult.data || [];
    const districts = districtsResult.data || [];
    const regions = regionsResult.data || [];

    // Build lookup maps for fast access
    const districtMap = new Map(districts.map(d => [d.id, d]));
    const regionMap = new Map(regions.map(r => [r.id, r]));

    // Build array of all minister associations to insert
    const allInserts: any[] = [];

    for (const request of requests) {
      const ministerIds: string[] = [];

      if (request.region_id) {
        // Region function request - get region authorities
        const region = regionMap.get(request.region_id);
        if (region) {
          [
            region.presbyter_id,
            region.vice_presbyter_id,
            region.secretary_id,
            region.treasurer_id,
            region.vocal1_id,
            region.vocal2_id,
          ].forEach((id: string) => { if (id) ministerIds.push(id); });
        }
      } else if (request.district_id) {
        // District function request - get district authorities
        const district = districtMap.get(request.district_id);
        if (district) {
          [
            district.superintendent_id,
            district.vice_superintendent_id,
            district.secretary_id,
            district.treasurer_id,
            district.vocal1_id,
            district.vocal2_id,
          ].forEach((id: string) => { if (id) ministerIds.push(id); });
        }
      }

      // Remove duplicates and add to batch insert
      const uniqueMinisterIds = Array.from(new Set(ministerIds));
      for (const ministerId of uniqueMinisterIds) {
        allInserts.push({
          credential_request_id: request.id,
          minister_id: ministerId,
        });
      }
    }

    // Batch delete: Remove all existing minister associations for these requests
    const requestIds = requests.map(r => r.id);
    if (requestIds.length > 0) {
      const { error: deleteError } = await supabase
        .from('credential_request_ministers')
        .delete()
        .in('credential_request_id', requestIds);

      if (deleteError) throw deleteError;
    }

    // Batch insert: Add all new minister associations in a single operation
    let synced = 0;
    if (allInserts.length > 0) {
      const { error: insertError } = await supabase
        .from('credential_request_ministers')
        .insert(allInserts as any) as { error: any };

      if (insertError) throw insertError;
      synced = requests.length; // Number of requests synced
    }

    // Also sync the nacional (DN) request
    await syncNacionalFunctionRequest(year);

    return { synced };
  } catch (error) {
    throw new DatabaseError('Failed to sync function request ministers', error);
  }
}

// ============================================================================
// INSTITUTION CREDENTIAL REQUESTS
// ============================================================================

import type { CredentialPerson } from '@/features/credential-requests/types';

export async function createInstitutionCredentialRequest(
  institutionId: string,
  year: number,
  userId: string,
): Promise<CredentialRequest> {
  try {
    const { data, error } = await supabase
      .from('credential_requests')
      .insert({
        institution_id: institutionId,
        credential_type: 'INSTITUTION',
        year,
        status: 'DRAFT',
        created_by: userId,
      } as never)
      .select('*')
      .single();
    if (error) throw error;
    return data as CredentialRequest;
  } catch (error) {
    throw new DatabaseError('Failed to create institution credential request', error);
  }
}

export async function getPersonsByCredentialRequest(requestId: string): Promise<CredentialPerson[]> {
  try {
    const { data, error } = await supabase
      .from('credential_request_ministers')
      .select(`
        id,
        minister_id,
        institution_member_id,
        minister:ministers(id, full_name, adb_code, dni, degree:degrees(id, name)),
        institution_member:institution_members(id, full_name, dni)
      `)
      .eq('credential_request_id', requestId);
    if (error) throw error;
    return (data ?? []).map((row: any): CredentialPerson => {
      if (row.minister_id && row.minister) {
        return { kind: 'minister', junctionId: row.id, id: row.minister.id, full_name: row.minister.full_name, adb_code: row.minister.adb_code, dni: row.minister.dni, degree: row.minister.degree };
      }
      return { kind: 'member', junctionId: row.id, id: row.institution_member.id, full_name: row.institution_member.full_name, dni: row.institution_member.dni };
    });
  } catch (error) {
    throw new DatabaseError('Failed to fetch persons for credential request', error);
  }
}

export async function addPersonToCredentialRequest(
  requestId: string,
  person: { ministerId: string } | { institutionMemberId: string },
): Promise<void> {
  try {
    const record = 'ministerId' in person
      ? { credential_request_id: requestId, minister_id: person.ministerId }
      : { credential_request_id: requestId, institution_member_id: person.institutionMemberId };
    const { error } = await supabase.from('credential_request_ministers').insert(record as never);
    if (error) throw error;
  } catch (error) {
    throw new DatabaseError('Failed to add person to credential request', error);
  }
}

export async function removePersonFromCredentialRequest(
  requestId: string,
  person: { ministerId?: string; institutionMemberId?: string },
): Promise<void> {
  try {
    let query = supabase.from('credential_request_ministers').delete().eq('credential_request_id', requestId);
    if (person.ministerId) query = query.eq('minister_id', person.ministerId) as typeof query;
    else if (person.institutionMemberId) query = query.eq('institution_member_id', person.institutionMemberId) as typeof query;
    const { error } = await query;
    if (error) throw error;
  } catch (error) {
    throw new DatabaseError('Failed to remove person from credential request', error);
  }
}

// ============================================================================
// DIRECTORIO NACIONAL CREDENTIAL REQUESTS
// ============================================================================

export async function getNextAvailableCodeNumberByPrefix(year: number, prefix: string): Promise<number> {
  try {
    const yearSuffix = year.toString().slice(-2);
    const codePrefix = `SOL${yearSuffix}-${prefix}-`;
    const { data, error } = await supabase
      .from('credential_requests')
      .select('code')
      .eq('year', year)
      .not('code', 'is', null)
      .like('code', `${codePrefix}%`)
      .order('code', { ascending: false })
      .limit(1);
    if (error) throw error;
    if (!data || data.length === 0) return 1;
    const lastCode = (data[0] as { code: string | null }).code;
    const match = lastCode?.match(/-(\d+)$/);
    return match ? parseInt(match[1], 10) + 1 : 1;
  } catch (error) {
    throw new DatabaseError('Failed to get next code number by prefix', error);
  }
}

export async function createNacionalFunctionRequest(
  year: number,
  userId: string,
  institutionId: string,
): Promise<CredentialRequest> {
  try {
    const nextNum = await getNextAvailableCodeNumberByPrefix(year, 'DN');
    const yearSuffix = year.toString().slice(-2);
    const code = `SOL${yearSuffix}-DN-${String(nextNum).padStart(2, '0')}`;
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('credential_requests')
      .insert({
        institution_id: institutionId,
        credential_type: 'FUNCTION',
        year,
        status: 'APPROVED',
        code,
        created_by: userId,
        approved_by: userId,
        approved_at: now,
        district_id: null,
        region_id: null,
      } as never)
      .select('*')
      .single();
    if (error) throw error;
    return data as CredentialRequest;
  } catch (error) {
    throw new DatabaseError('Failed to create nacional function request', error);
  }
}

export async function syncNacionalFunctionRequest(year: number): Promise<void> {
  try {
    // Find the DN FUNCTION request for this year (no district, no region, has institution_id)
    const { data: requests, error: reqError } = await supabase
      .from('credential_requests')
      .select('id, institution_id')
      .eq('credential_type', 'FUNCTION')
      .eq('year', year)
      .is('district_id', null)
      .is('region_id', null)
      .not('institution_id', 'is', null);
    if (reqError) throw reqError;
    if (!requests || requests.length === 0) return;

    for (const request of requests) {
      const [nationalResult, officeResult] = await Promise.all([
        // Current national board ministers (Presidente, Vice, Secretario, Tesorero)
        supabase
          .from('authority_positions')
          .select('minister_id')
          .eq('org_type', 'NATIONAL')
          .is('ended_at', null) as unknown as Promise<{ data: any[] | null; error: any }>,
        // Current office authorities (institution_members of DN)
        supabase
          .from('institution_authority_positions')
          .select('institution_member_id, minister_id')
          .eq('institution_id', request.institution_id!)
          .is('ended_at', null) as unknown as Promise<{ data: any[] | null; error: any }>,
      ]);
      if (nationalResult.error) throw nationalResult.error;
      if (officeResult.error) throw officeResult.error;

      // Delete all existing entries for this request
      const { error: delError } = await supabase
        .from('credential_request_ministers')
        .delete()
        .eq('credential_request_id', request.id);
      if (delError) throw delError;

      // Build inserts: national ministers + office members
      const inserts: any[] = [];
      for (const row of nationalResult.data ?? []) {
        if (row.minister_id) inserts.push({ credential_request_id: request.id, minister_id: row.minister_id });
      }
      for (const row of officeResult.data ?? []) {
        if (row.institution_member_id) inserts.push({ credential_request_id: request.id, institution_member_id: row.institution_member_id });
        else if (row.minister_id) inserts.push({ credential_request_id: request.id, minister_id: row.minister_id });
      }

      if (inserts.length > 0) {
        const { error: insError } = await supabase.from('credential_request_ministers').insert(inserts as any);
        if (insError) throw insError;
      }
    }
  } catch (error) {
    throw new DatabaseError('Failed to sync nacional function request', error);
  }
}
